'use client';

import { useEffect, useState } from 'react';
import { getBlogs } from '@/lib/api';

type Post = {
  category: string;
  date: string;
  title: string;
  excerpt: string;
  img: string;
  readTime: string;
  href: string;
};

const FALLBACK_POSTS: Post[] = [
  {
    category: 'Funding',
    date: '10 Mar 2024',
    title: 'How to Secure Business Funding in 2024: A Complete Guide',
    excerpt: 'Discover the top funding strategies from government schemes to venture capital to fuel your business growth this year.',
    img: 'https://images.unsplash.com/photo-1579621970563-ebec7560ff3e?w=700&q=75',
    readTime: '2 min read',
    href: '/blog/how-to-secure-business-funding-2024',
  },
  {
    category: 'IT',
    date: '18 Mar 2024',
    title: 'Digital Transformation: Why Every Business Needs an IT Strategy',
    excerpt: 'Digital transformation is no longer optional. Learn how a robust IT strategy can streamline operations, reduce costs, and accelerate growth.',
    img: 'https://images.unsplash.com/photo-1518770660439-4636190af475?w=700&q=75',
    readTime: '2 min read',
    href: '/blog/digital-transformation-it-strategy-for-business',
  },
  {
    category: 'Legal',
    date: '25 Mar 2024',
    title: 'Essential Legal Compliance for Indian Startups',
    excerpt: 'From GST registration to labour law compliance, we break down the essential legal requirements every Indian startup must fulfil.',
    img: 'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=700&q=75',
    readTime: '2 min read',
    href: '/blog/essential-legal-compliance-indian-startups',
  },
  {
    category: 'Business',
    date: '1 Apr 2024',
    title: 'Top Business Strategies to Scale Your Startup in 2024',
    excerpt: 'Learn the proven strategies that successful founders use to grow their startups from zero to scale in competitive Indian markets.',
    img: 'https://images.unsplash.com/photo-1521737852567-6949f3f9f2b5?w=700&q=75',
    readTime: '3 min read',
    href: '/blog',
  },
];

export function BlogPreview() {
  const [posts, setPosts] = useState<Post[]>(FALLBACK_POSTS);

  useEffect(() => {
    getBlogs(4, 1)
      .then((res) => {
        if (res.data.length > 0) {
          setPosts(
            res.data.map((p) => ({
              category: p.category,
              date: new Date(p.publishedAt ?? p.createdAt).toLocaleDateString('en-IN', {
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              }),
              title: p.title,
              excerpt: p.excerpt,
              img: p.thumbnail || 'https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=700&q=75',
              readTime: `${p.readingTime} min read`,
              href: `/blog/${p.slug}`,
            }))
          );
        }
      })
      .catch(() => { /* keep fallback */ });
  }, []);

  return (
    <section className="bg-white py-16 lg:py-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="h-px w-8 bg-amber-500" />
              <span className="text-amber-600 text-xs font-semibold tracking-[0.18em] uppercase">Insights &amp; Resources</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900" style={{ fontFamily: "'Sora', sans-serif" }}>
              Expert Business Insights
            </h2>
          </div>
          <a
            href="/blog"
            className="self-start sm:self-auto inline-flex items-center gap-2 text-sm font-semibold text-amber-600 border-b border-amber-300 pb-0.5 hover:border-amber-600 transition-colors duration-200 whitespace-nowrap"
          >
            All Articles
          </a>
        </div>

        {/* 4 uniform cards in one row */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {posts.map((post) => (
            <a
              key={post.href}
              href={post.href}
              className="group flex flex-col rounded-sm overflow-hidden border border-slate-100 bg-white shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              {/* Image */}
              <div className="relative h-44 overflow-hidden flex-shrink-0">
                <img
                  src={post.img}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
                <span className="absolute top-3 left-3 px-2.5 py-0.5 bg-amber-500 text-white text-[10px] font-bold rounded-full tracking-wide uppercase">
                  {post.category}
                </span>
              </div>

              {/* Content */}
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-center gap-2 text-[11px] text-slate-400 mb-2">
                  <span>{post.date}</span>
                  <span className="w-1 h-1 rounded-full bg-slate-300" />
                  <span>{post.readTime}</span>
                </div>
                <h3
                  className="text-slate-900 font-bold text-sm leading-snug mb-2 group-hover:text-amber-600 transition-colors line-clamp-2"
                  style={{ fontFamily: "'Sora', sans-serif" }}
                >
                  {post.title}
                </h3>
                <p className="text-slate-500 text-xs leading-relaxed flex-1 line-clamp-3">
                  {post.excerpt}
                </p>
                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <span className="text-amber-600 text-xs font-semibold group-hover:underline">
                    Read Article
                  </span>
                  <svg className="w-4 h-4 text-amber-500 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>
            </a>
          ))}
        </div>

      </div>
    </section>
  );
}